import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Leaf, Users2, Scale, ClipboardCheck, Database, FileWarning, ListTodo, ArrowRight, ClipboardList,
  Landmark, Sparkles, FileText,
} from "lucide-react";
import { useTenant } from "@/context/TenantContext";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { NoDataBadge, SizeBadge, PeriodStatusBadge, DemoBadge } from "@/components/badges";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SUBMISSION_STATUS, QUESTIONNAIRE_STATE_LABELS, formatDisplayName } from "@/lib/strings";
import { CorrectionsPanel } from "@/components/CorrectionsPanel";
import api from "@/lib/api";

function WidgetCard({ title, icon: Icon, testid, children, accent }) {
  return (
    <Card className="border-slate-200" data-testid={testid}>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <span className={`w-8 h-8 rounded-md flex items-center justify-center ${accent || "bg-slate-100"}`}>
            <Icon className="w-4 h-4" aria-hidden="true" />
          </span>
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function WidgetSkeleton({ testid }) {
  return (
    <div className="space-y-3 animate-pulse" data-testid={testid} aria-busy="true" aria-live="polite">
      <div className="h-9 w-24 rounded bg-slate-200" />
      <div className="h-3 w-full rounded bg-slate-100" />
      <div className="h-3 w-2/3 rounded bg-slate-100" />
    </div>
  );
}

function PillarCard({ label, letter, colors, testid, stats, link }) {
  const { t } = useI18n();
  return (
    <div className={`rounded-lg border p-4 ${colors}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-semibold">{letter} — {label}</p>
        {stats === null && <NoDataBadge />}
      </div>
      {stats === undefined ? (
        <WidgetSkeleton testid={`${testid}-loading`} />
      ) : stats ? (
        <>
          <p className="text-3xl font-extrabold mb-2" data-testid={`${testid}-value`}>{stats.completion}%</p>
          <div className="h-2 rounded-full bg-white/70 border border-slate-200">
            <div className="h-full rounded-full bg-current opacity-70" style={{ width: `${stats.completion}%` }} />
          </div>
          <p className="text-xs mt-2 opacity-80">
            {t("dash.pillarStats", { completed: stats.completed, missing: stats.missing })}
          </p>
          {link && (
            <Link to={link} className="inline-flex items-center gap-1 text-xs font-semibold hover:underline mt-1" data-testid={`${testid}-link`}>
              {t("dash.openEngine")} <ArrowRight className="w-3 h-3" aria-hidden="true" />
            </Link>
          )}
        </>
      ) : (
        <>
          <p className="text-3xl font-extrabold text-slate-400 mb-2" data-testid={`${testid}-value`}>—</p>
          <div className="h-2 rounded-full bg-white/70 border border-slate-200">
            <div className="h-full w-0 rounded-full" />
          </div>
          <p className="text-xs text-slate-600 mt-2">{t("dash.pillarEmpty")}</p>
        </>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { company, companyId, period, can } = useTenant();
  const { t } = useI18n();
  const isConsultantView = can("analysis:score:view");
  const [usersCount, setUsersCount] = useState(null);
  const [periodsCount, setPeriodsCount] = useState(null);
  const [vsme, setVsme] = useState(undefined);
  const [questionnaire, setQuestionnaire] = useState(undefined);
  const [mef, setMef] = useState(undefined);
  const [mefComp, setMefComp] = useState(undefined);
  const [score, setScore] = useState(undefined);
  const [docsCount, setDocsCount] = useState(null);
  const [docRequests, setDocRequests] = useState(null);
  const [docCoverage, setDocCoverage] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [planOpen, setPlanOpen] = useState(null);
  const [govSections, setGovSections] = useState(null);
  const [sharedReports, setSharedReports] = useState(null);
  const [extq, setExtq] = useState(null);
  const navigate = useNavigate();
  const questionnaireSummary = questionnaire?.summary;
  const corrections = questionnaireSummary?.corrections || [];
  const blockingCount = questionnaireSummary?.blocking_inconsistencies ?? corrections.length;
  const effectiveStatus = questionnaireSummary?.effective_status || submission?.submission_status || "DRAFT";
  const canFixCorrections = !questionnaire?.read_only
    || ["INTEGRATION_REQUIRED", "READY_FOR_RESUBMISSION"].includes(effectiveStatus);
  const questionnaireReadOnly = Boolean(questionnaire?.read_only) && !canFixCorrections;
  const questionnaireCta = questionnaireReadOnly
    ? "Visualizza questionario"
    : t("dash.continue");

  const loadQuestionnaireProgress = useCallback(async () => {
    if (!companyId || !period?.id) return;
    try {
      const { data } = await api.get(`/companies/${companyId}/periods/${period.id}/questionnaire/progress`);
      setQuestionnaire(data);
    } catch {
      setQuestionnaire(null);
    }
  }, [companyId, period?.id]);

  useEffect(() => { loadQuestionnaireProgress(); }, [loadQuestionnaireProgress]);

  useEffect(() => {
    if (!companyId || !period?.id) return;
    api.get(`/companies/${companyId}/periods/${period.id}/vsme`)
      .then((r) => setVsme(r.data))
      .catch(() => setVsme(null));
    api.get(`/companies/${companyId}/periods/${period.id}/mef/summary`)
      .then((r) => setMef(r.data))
      .catch(() => setMef(null));
    // FASE 2/5: per l'utente aziendale lo stato di compilazione delle domande MEF
    // viene dall'endpoint dedicato (non dall'analisi dossier del consulente).
    if (!can("analysis:dossier:view")) {
      api.get(`/companies/${companyId}/periods/${period.id}/mef/compilazione`)
        .then((r) => setMefComp(r.data))
        .catch(() => setMefComp(null));
    }
    if (can("analysis:score:view")) {
      api.get(`/companies/${companyId}/periods/${period.id}/score-runs/current`)
        .then((r) => setScore(r.data))
        .catch(() => setScore({ error: true }));
      api.get(`/companies/${companyId}/periods/${period.id}/improvement-plan`)
        .then((r) => setPlanOpen(r.data.summary?.open ?? 0))
        .catch(() => setPlanOpen(null));
    }
    api.get(`/companies/${companyId}/periods/${period.id}/documents`)
      .then((r) => setDocsCount(r.data.length))
      .catch(() => setDocsCount(null));
    api.get(`/companies/${companyId}/periods/${period.id}/document-requests`)
      .then((r) => setDocRequests((r.data || []).filter((x) => x.status === "OPEN").length))
      .catch(() => setDocRequests(null));
    api.get(`/companies/${companyId}/periods/${period.id}/evidence-coverage`)
      .then((r) => setDocCoverage(r.data))
      .catch(() => setDocCoverage(null));
    api.get(`/companies/${companyId}/periods/${period.id}/submission`)
      .then((r) => setSubmission(r.data))
      .catch(() => setSubmission(null));
    if (!can("analysis:report:view")) {
      api.get(`/companies/${companyId}/periods/${period.id}/reports`)
        .then((r) => setSharedReports(r.data.reports || []))
        .catch(() => setSharedReports(null));
    }
    api.get(`/companies/${companyId}/periods/${period.id}/external-questionnaires`)
      .then((r) => {
        const list = r.data.instances || [];
        setExtq({
          total: list.length,
          open: list.filter((x) => !["APPROVED", "EXPORTED", "ARCHIVED"].includes(x.status)),
          awaiting: list.filter((x) => ["SUBMITTED_BY_COMPANY", "UNDER_CONSULTANT_REVIEW"].includes(x.status)).length,
        });
      })
      .catch(() => setExtq(null));
    api.get(`/companies/${companyId}/periods/${period.id}/governance/summary`)
      .then((r) => {
        const sections = r.data.sections || {};
        const total = Object.values(sections).filter((s) => s.complete + s.to_verify + s.to_complete + s.not_applicable > 0).length;
        // FASE 8 (utente): separare "dati inseriti" da "interamente verificate".
        const entered = Object.values(sections).filter((s) => s.to_complete === 0 && (s.complete + s.to_verify + s.not_applicable) > 0).length;
        const verified = Object.values(sections).filter((s) => s.to_complete === 0 && s.to_verify === 0 && (s.complete + s.not_applicable) > 0).length;
        setGovSections({ entered, verified, total });
      })
      .catch(() => setGovSections(null));
  }, [companyId, period?.id, can]);

  useEffect(() => {
    if (!companyId) return;
    if (can("users:read")) {
      api.get(`/companies/${companyId}/users`)
        .then((r) => setUsersCount(r.data.length))
        .catch(() => setUsersCount(null));
    }
    api.get(`/companies/${companyId}/periods`)
      .then((r) => setPeriodsCount(r.data.length))
      .catch(() => setPeriodsCount(null));
  }, [companyId, can]);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
            {t("dash.title")}
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            {t("dash.welcome", { name: formatDisplayName(user?.full_name) })}{" "}
            <span className="font-semibold text-slate-800">{company?.denominazione}</span>.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SizeBadge size={company?.dimensione_pmi} />
          {company?.is_demo && <DemoBadge />}
          {period && (
            <span className="flex items-center gap-2 text-sm text-slate-700">
              {t("dash.activePeriod")} <strong>{t("dash.fiscalYear", { year: period.year })}</strong>
              <PeriodStatusBadge status={period.status} />
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
        {isConsultantView ? (
        <WidgetCard title={t("dash.widget.score")} icon={Scale} testid="widget-esg-score" accent="bg-green-100 text-green-800">
          {score === undefined ? (
            <WidgetSkeleton testid="esg-score-loading" />
          ) : score?.error ? (
            <p className="text-xs text-red-700" data-testid="esg-score-error">{t("dash.scoreError")}</p>
          ) : score?.run?.display_status === "INSUFFICIENT_DATA" ? (
            <div className="space-y-2">
              <div className="flex items-end gap-2 flex-wrap">
                <p className="text-4xl font-extrabold text-slate-400" data-testid="esg-score-value">—</p>
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded mb-1 bg-red-100 text-red-700" data-testid="esg-score-status">
                  {t("dash.scoreStatus.INSUFFICIENT_DATA")}
                </span>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                {t("dash.scoreInsufficient", { coverage: score.run.coverage_percentage ?? 0 })}
              </p>
              <dl className="text-xs space-y-1 text-slate-700">
                {[["E", t("dash.pillar.e")], ["S", t("dash.pillar.s")], ["G", t("dash.pillar.g")]].map(([k, label]) => (
                  <div className="flex justify-between" key={k}>
                    <dt>{label}</dt>
                    <dd className="font-semibold text-slate-400" data-testid={`esg-score-pillar-${k.toLowerCase()}`}>—</dd>
                  </div>
                ))}
              </dl>
              <Link to="/valutazione" className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 hover:underline" data-testid="goto-valutazione">
                {t("dash.openValutazione")} <ArrowRight className="w-3 h-3" aria-hidden="true" />
              </Link>
            </div>
          ) : score?.run ? (
            <div className="space-y-2">
              <div className="flex items-end gap-2 flex-wrap">
                <p className="text-4xl font-extrabold text-green-800" data-testid="esg-score-value">{score.run.overall_score ?? "—"}</p>
                <span className="text-sm text-slate-500 mb-1">/100</span>
                {(() => {
                  const ds = score.run.display_status || "CURRENT";
                  const cls = {
                    CURRENT: "bg-green-100 text-green-800", STALE: "bg-amber-100 text-amber-800",
                    INSUFFICIENT_DATA: "bg-red-100 text-red-700", CALCULATING: "bg-sky-100 text-sky-800",
                    FAILED: "bg-red-100 text-red-700",
                  }[ds] || "bg-slate-100 text-slate-600";
                  return (
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded mb-1 ${cls}`} data-testid="esg-score-status">
                      {t(`dash.scoreStatus.${ds}`)}
                    </span>
                  );
                })()}
              </div>
              <dl className="text-xs space-y-1 text-slate-700">
                {[["E", t("dash.pillar.e"), "environmental_score"], ["S", t("dash.pillar.s"), "social_score"], ["G", t("dash.pillar.g"), "governance_score"]].map(([k, label, field]) => (
                  <div className="flex justify-between" key={k}>
                    <dt>{label}</dt>
                    <dd className="font-semibold" data-testid={`esg-score-pillar-${k.toLowerCase()}`}>
                      {score.run[field] ?? "—"}
                    </dd>
                  </div>
                ))}
              </dl>
              {score.run.display_status === "STALE" && (
                <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="esg-score-stale-note">
                  {t("dash.scoreStale")}
                </p>
              )}
              <p className="text-[11px] text-slate-500 leading-snug">
                {t("dash.scoreSourceNote")}
              </p>
              <Link to="/valutazione" className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 hover:underline" data-testid="goto-valutazione">
                {t("dash.openValutazione")} <ArrowRight className="w-3 h-3" aria-hidden="true" />
              </Link>
            </div>
          ) : (
            <div>
              <div className="flex items-end gap-3">
                <p className="text-4xl font-extrabold text-slate-400" data-testid="esg-score-value">—</p>
                <NoDataBadge />
              </div>
              <p className="text-xs text-slate-600 mt-3 leading-relaxed">
                {t("dash.scoreEmpty")}
              </p>
              <Link to="/valutazione" className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 hover:underline mt-1" data-testid="goto-valutazione">
                {t("dash.openValutazione")} <ArrowRight className="w-3 h-3" aria-hidden="true" />
              </Link>
            </div>
          )}
        </WidgetCard>
        ) : (
        <WidgetCard title={t("dash.widget.journey")} icon={ClipboardCheck} testid="widget-compile-status" accent="bg-green-100 text-green-800">
          {questionnaire === undefined || vsme === undefined ? (
            <WidgetSkeleton testid="compile-loading" />
          ) : questionnaire || vsme ? (
            <div className="space-y-2">
              <div className="flex items-center gap-3" title="Percentuale di risposte ESG valide (coerenti e senza controlli falliti) sulle domande applicabili">
                <div className="flex-1 h-3 rounded-full bg-slate-100 border border-slate-200" data-testid="compile-progress-bar">
                  <div className="h-full rounded-full bg-green-700" style={{ width: `${questionnaireSummary?.valid_completion_percentage ?? 0}%` }} />
                </div>
                <span className="text-sm font-bold text-slate-900" data-testid="compile-completion-value">{questionnaireSummary?.valid_completion_percentage ?? 0}%</span>
              </div>
              <dl className="text-xs space-y-1 text-slate-700">
                <div className="flex justify-between"><dt>{t("dash.validAnswersPct")}</dt><dd className="font-semibold" data-testid="compile-valid-answers">{questionnaireSummary?.valid ?? 0}/{questionnaireSummary?.applicable ?? 0}</dd></div>
                <div className="flex justify-between"><dt>{t("dash.missing")}</dt><dd className="font-semibold" data-testid="compile-missing-count">{questionnaireSummary?.missing ?? vsme?.overall?.missing ?? "—"}</dd></div>
                <div className="flex justify-between"><dt>{t("dash.toVerify")}</dt><dd className="font-semibold" data-testid="compile-toverify-count">{questionnaireSummary?.to_verify ?? vsme?.overall?.to_verify ?? "—"}</dd></div>
                <div className="flex justify-between"><dt>Incongruenze bloccanti</dt><dd className="font-semibold text-red-700" data-testid="compile-blocking-count">{blockingCount}</dd></div>
                <div className="flex justify-between"><dt>{t("dash.docsUploaded")}</dt><dd className="font-semibold">{docsCount ?? "—"}</dd></div>
                <div className="flex justify-between" title="Aree di governance con tutti i dati inseriti (base: aree, non datapoint)"><dt>{t("dash.govSections")}</dt><dd className="font-semibold" data-testid="compile-governance-count">{govSections ? `${govSections.entered}/${govSections.total}` : "—"}</dd></div>
                <div className="flex justify-between"><dt>{t("dash.govSectionsVerified")}</dt><dd className="font-semibold" data-testid="compile-governance-verified">{govSections ? `${govSections.verified}/${govSections.total}` : "—"}</dd></div>
              </dl>
              <Link to="/questionario" className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 hover:underline" data-testid="goto-questionnaire-compile">
                {questionnaireCta} <ArrowRight className="w-3 h-3" aria-hidden="true" />
              </Link>
            </div>
          ) : (
            <NoDataBadge />
          )}
        </WidgetCard>
        )}

        <WidgetCard title={t("dash.widget.vsme")} icon={ClipboardCheck} testid="widget-vsme-completion" accent="bg-indigo-100 text-indigo-800">
          {vsme === undefined ? (
            <WidgetSkeleton testid="vsme-loading" />
          ) : vsme ? (
            <div className="space-y-2">
              <div className="flex items-center gap-3" title="Percentuale di datapoint VSME disponibili sui datapoint applicabili (fonte: motore VSME)">
                <div className="flex-1 h-3 rounded-full bg-slate-100 border border-slate-200" data-testid="vsme-progress-bar">
                  <div className="h-full rounded-full bg-green-700" style={{ width: `${vsme.overall.completion}%` }} />
                </div>
                <span className="text-sm font-bold text-slate-900" data-testid="vsme-completion-value">{vsme.overall.completion}%</span>
              </div>
              <dl className="text-xs space-y-1 text-slate-700">
                <div className="flex justify-between"><dt>{t("dash.vsmeDatapointsPct")}</dt><dd className="font-semibold" data-testid="vsme-datapoints-label-value">{vsme.overall.completion}%</dd></div>
                <div className="flex justify-between"><dt>Basic</dt><dd className="font-semibold" data-testid="vsme-basic-completion">{vsme.modules.BASIC.completion}%</dd></div>
                <div className="flex justify-between"><dt>Comprehensive</dt><dd className="font-semibold" data-testid="vsme-comprehensive-completion">{vsme.comprehensive_active ? `${vsme.modules.COMPREHENSIVE.completion}%` : t("dash.notActive")}</dd></div>
                <div className="flex justify-between"><dt>{t("dash.missingData")}</dt><dd className="font-semibold" data-testid="vsme-missing-count">{vsme.overall.missing}</dd></div>
                <div className="flex justify-between"><dt>{t("dash.toVerify")}</dt><dd className="font-semibold">{vsme.overall.to_verify}</dd></div>
                <div className="flex justify-between"><dt>{t("dash.approved")}</dt><dd className="font-semibold">{vsme.overall.approved}</dd></div>
              </dl>
              <Link to="/vsme" className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 hover:underline" data-testid="goto-vsme">
                {t("dash.openVsme")} <ArrowRight className="w-3 h-3" aria-hidden="true" />
              </Link>
            </div>
          ) : (
            <NoDataBadge />
          )}
        </WidgetCard>

        <WidgetCard title={t("dash.widget.mef")} icon={Landmark} testid="widget-mef-completion" accent="bg-amber-100 text-amber-800">
          {isConsultantView ? (
            mef === undefined ? (
            <WidgetSkeleton testid="mef-loading" />
            ) : mef ? (
            <div className="space-y-2">
              <div className="flex items-center gap-3" title={t("dash.tipMefPct")}>
                <div className="flex-1 h-3 rounded-full bg-slate-100 border border-slate-200" data-testid="mef-progress-bar">
                  <div className="h-full rounded-full bg-green-700" style={{ width: `${mef.stats.overall.completion}%` }} />
                </div>
                <span className="text-sm font-bold text-slate-900" data-testid="mef-completion-value">{mef.stats.overall.completion}%</span>
              </div>
              <dl className="text-xs space-y-1 text-slate-700">
                <div className="flex justify-between"><dt>{t("dash.p1")}</dt><dd className="font-semibold" data-testid="mef-p1-completion">{mef.stats.priority_1.completion}%</dd></div>
                <div className="flex justify-between"><dt>{t("dash.coveredVsme")}</dt><dd className="font-semibold" data-testid="mef-covered-count">{mef.stats.overall.covered}</dd></div>
                <div className="flex justify-between"><dt>{t("dash.toIntegrate")}</dt><dd className="font-semibold">{mef.stats.overall.missing}</dd></div>
              </dl>
              <Link to="/pmi-banche" className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 hover:underline" data-testid="goto-mef">
                {t("dash.openMef")} <ArrowRight className="w-3 h-3" aria-hidden="true" />
              </Link>
            </div>
            ) : (
              <NoDataBadge />
            )
          ) : mefComp === undefined ? (
            <WidgetSkeleton testid="mef-loading" />
          ) : mefComp ? (
            <div className="space-y-2">
              <div className="flex items-center gap-3" title={t("dash.tipQuestionsPct")}>
                <div className="flex-1 h-3 rounded-full bg-slate-100 border border-slate-200" data-testid="mef-progress-bar">
                  <div className="h-full rounded-full bg-green-700" style={{ width: `${mefComp.summary.percentage}%` }} />
                </div>
                <span className="text-sm font-bold text-slate-900" data-testid="mef-completion-value">{mefComp.summary.percentage}%</span>
              </div>
              <dl className="text-xs space-y-1 text-slate-700">
                <div className="flex justify-between"><dt>{t("dash.mefValidPct")}</dt><dd className="font-semibold" data-testid="mef-valid-count">{mefComp.summary.valid}/{mefComp.summary.total} · {mefComp.summary.percentage}%</dd></div>
                <div className="flex justify-between"><dt>Risposte presenti</dt><dd className="font-semibold" data-testid="mef-answered-count">{mefComp.summary.answered}/{mefComp.summary.total}</dd></div>
                <div className="flex justify-between" title={t("dash.tipMefPct")}><dt>{t("dash.mefCoverage")}</dt><dd className="font-semibold" data-testid="mef-coverage-value">{mef ? `${mef.stats.overall.completion}%` : "—"}</dd></div>
                <div className="flex justify-between"><dt>Incongruenze bloccanti (totale questionario)</dt><dd className="font-semibold text-red-700" data-testid="mef-inconsistent-count">{mefComp.questionnaire_summary?.blocking_inconsistencies ?? blockingCount}</dd></div>
              </dl>
              <Link to={mefComp.summary.first_incomplete ? `/questionario-mef?domanda=${mefComp.summary.first_incomplete}` : "/questionario-mef/riepilogo"}
                className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 hover:underline" data-testid="goto-mef">
                {mefComp.read_only && !canFixCorrections ? "Visualizza questionario" : t("dash.compileMef")} <ArrowRight className="w-3 h-3" aria-hidden="true" />
              </Link>
            </div>
          ) : (
            <NoDataBadge />
          )}
        </WidgetCard>

        <WidgetCard title={t("dash.widget.companyData")} icon={Database} testid="widget-company-data" accent="bg-sky-100 text-sky-800">
          <dl className="text-sm space-y-1.5">
            <div className="flex justify-between"><dt className="text-slate-600">{t("dash.sector")}</dt><dd className="font-medium text-slate-900" data-testid="company-sector">{company?.settore_display || company?.settore || (company?.codice_ateco ? `ATECO ${company.codice_ateco}` : "—")}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-600">{t("dash.employees")}</dt><dd className="font-medium text-slate-900" data-testid="company-employees">{company?.numero_dipendenti ?? "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-600">{t("dash.managedUsers")}</dt><dd className="font-medium text-slate-900" data-testid="users-count">{usersCount ?? "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-600">{t("dash.esgPeriods")}</dt><dd className="font-medium text-slate-900" data-testid="periods-count">{periodsCount ?? "—"}</dd></div>
          </dl>
        </WidgetCard>
      </div>

      <Card className="border-green-200 bg-gradient-to-br from-green-50 to-white" data-testid="widget-ask-eva">
        <CardContent className="p-5">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-md bg-green-700 flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5 text-white" aria-hidden="true" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900">{t("dash.askEva")}</p>
              <p className="text-xs text-slate-600">{t("dash.askEvaSub")}</p>
            </div>
          </div>
          <form className="mt-3 flex gap-2" onSubmit={(e) => {
            e.preventDefault();
            const q = e.target.elements["eva-q"].value.trim();
            if (q) navigate(`/eva?q=${encodeURIComponent(q)}`);
          }}>
            <input name="eva-q" type="text" placeholder={t("dash.askEvaPlaceholder")}
              className="flex-1 h-9 rounded-md border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-600"
              data-testid="ask-eva-input" aria-label={t("dash.askEva")} />
            <button type="submit"
              className="h-9 px-4 rounded-md bg-green-700 hover:bg-green-800 text-white text-sm font-semibold transition-colors"
              data-testid="ask-eva-submit">
              {t("dash.askEvaSubmit")}
            </button>
          </form>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="widget-esg-pillars">
        <PillarCard letter="E" label={t("dash.pillar.e")} testid="pillar-environmental"
          colors="bg-green-50 border-green-200 text-green-900"
          stats={vsme === undefined ? undefined : vsme?.categories?.ENVIRONMENTAL ?? null} link="/environmental" />
        <PillarCard letter="S" label={t("dash.pillar.s")} testid="pillar-social"
          colors="bg-sky-50 border-sky-200 text-sky-900"
          stats={vsme === undefined ? undefined : vsme?.categories?.SOCIAL ?? null} link="/social" />
        <PillarCard letter="G" label={t("dash.pillar.g")} testid="pillar-governance"
          colors="bg-indigo-50 border-indigo-200 text-indigo-900"
          stats={vsme === undefined ? undefined : vsme?.categories?.GOVERNANCE ?? null} link="/governance" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <WidgetCard title={t("dash.widget.dataStatus")} icon={Database} testid="widget-data-status" accent="bg-slate-200 text-slate-700">
          {vsme === undefined ? (
            <WidgetSkeleton testid="data-status-loading" />
          ) : vsme ? (
            <dl className="text-sm space-y-1.5">
              <div className="flex justify-between"><dt className="text-slate-600">{t("dash.verified")}</dt><dd className="font-semibold text-green-800" data-testid="data-verified-count">{vsme.overall.verified + vsme.overall.approved}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-600">{t("dash.missingData")}</dt><dd className="font-semibold text-amber-800" data-testid="data-missing-count">{vsme.overall.missing}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-600">{t("dash.toVerify")}</dt><dd className="font-semibold text-slate-900">{vsme.overall.to_verify}</dd></div>
            </dl>
          ) : (
            <div className="flex items-center gap-3">
              <p className="text-3xl font-extrabold text-slate-400">—</p>
              <NoDataBadge />
            </div>
          )}
        </WidgetCard>
        <WidgetCard title={t("dash.widget.locker")} icon={FileWarning} testid="widget-missing-documents" accent="bg-slate-200 text-slate-700">
          <div className="flex items-center gap-3">
            <p className="text-3xl font-extrabold text-slate-900" data-testid="documents-count">{docsCount ?? "—"}</p>
            <p className="text-xs text-slate-600">{t("dash.docsInPeriod")}</p>
          </div>
          {docCoverage && docCoverage.filled_values > 0 && (
            <p className="text-xs text-slate-600 mt-2" data-testid="documents-coverage">
              {t("dash.docCoverage")} <strong>{docCoverage.overall}%</strong>
            </p>
          )}
          {docRequests > 0 && (
            <p className="text-xs font-semibold text-amber-800 mt-1" data-testid="documents-requests-count">
              {docRequests} {docRequests === 1 ? t("dash.docsRequestedOne") : t("dash.docsRequestedMany")}
            </p>
          )}
          <Link to="/documenti" className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 hover:underline mt-3" data-testid="goto-documents">
            {t("dash.openLocker")} <ArrowRight className="w-3 h-3" aria-hidden="true" />
          </Link>
        </WidgetCard>

        <WidgetCard title={isConsultantView ? t("dash.widget.openActions") : t("dash.widget.submission")} icon={ListTodo} testid="widget-open-actions" accent="bg-slate-200 text-slate-700">
          {isConsultantView ? (
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <p className="text-3xl font-extrabold text-slate-900" data-testid="open-actions-count">{planOpen ?? "—"}</p>
                <p className="text-xs text-slate-600">{t("dash.openActionsInPlan")}</p>
              </div>
              <Link to="/piano-miglioramento" className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 hover:underline" data-testid="goto-improvement-plan">
                {t("dash.openPlan")} <ArrowRight className="w-3 h-3" aria-hidden="true" />
              </Link>
            </div>
          ) : (
            <div className="space-y-2" data-testid="submission-widget">
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-600">{t("dash.submissionState")}</span>
                <Badge className={SUBMISSION_STATUS[effectiveStatus]?.cls} data-testid="submission-status-badge">
                  {t(`submissionStatus.${effectiveStatus}`) || SUBMISSION_STATUS[effectiveStatus]?.label}
                </Badge>
              </div>
              <p className="text-xs text-slate-700" data-testid="submission-approval-state">
                Approvazione: <strong>{period?.status === "APPROVED" ? "approvato" : "Non approvato"}</strong>
                {blockingCount > 0 && <> · Invio al consulente: <strong className="text-red-700">bloccato</strong></>}
              </p>
              <p className="text-sm text-slate-700">
                {t("dash.openRequests")}{" "}
                <strong className="text-amber-700" data-testid="open-requests-count">{submission?.summary?.open_requests ?? 0}</strong>
              </p>
              <Link to="/questionario" className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 hover:underline" data-testid="goto-questionnaire-submission">
                {questionnaireReadOnly ? "Visualizza questionario" : t("dash.gotoQuestionnaire")} <ArrowRight className="w-3 h-3" aria-hidden="true" />
              </Link>
            </div>
          )}
        </WidgetCard>

        {!isConsultantView && sharedReports && sharedReports.length > 0 && (
          <WidgetCard title={t("dash.widget.sharedReports")} icon={FileText}
            testid="widget-shared-reports" accent="bg-indigo-100 text-indigo-800">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <p className="text-3xl font-extrabold text-slate-900" data-testid="shared-reports-count">
                  {sharedReports.length}
                </p>
                <p className="text-xs text-slate-600">{t("dash.sharedReportsCount")}</p>
              </div>
              <ul className="space-y-1.5">
                {sharedReports.slice(0, 3).map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-2"
                    data-testid={`shared-report-${r.id}`}>
                    <span className="text-xs text-slate-700 truncate">
                      {r.template_title} · v{r.version_number}
                    </span>
                    <button type="button"
                      className="text-xs font-semibold text-green-700 hover:underline shrink-0"
                      onClick={async () => {
                        try {
                          const resp = await api.get(
                            `/companies/${companyId}/periods/${period.id}/reports/${r.id}/download`,
                            { responseType: "blob" });
                          const url = URL.createObjectURL(resp.data);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = `${r.template_code}_${period.year}_v${r.version_number}.pdf`;
                          a.click();
                          URL.revokeObjectURL(url);
                        } catch { /* permessi gestiti lato backend */ }
                      }}
                      data-testid={`shared-report-download-${r.id}`}>
                      {t("dash.downloadPdf")}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </WidgetCard>
        )}

        {extq && extq.total > 0 && (
          <WidgetCard title={isConsultantView ? t("dash.widget.extq") : t("dash.widget.extqTodo")}
            icon={ClipboardList} testid="widget-external-questionnaires"
            accent="bg-sky-100 text-sky-800">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <p className="text-3xl font-extrabold text-slate-900" data-testid="extq-open-count">
                  {isConsultantView ? extq.awaiting : extq.open.length}
                </p>
                <p className="text-xs text-slate-600">
                  {isConsultantView
                    ? t("dash.extqAwaiting")
                    : extq.open.length === 1
                      ? t("dash.extqOne")
                      : t("dash.extqMany")}
                </p>
              </div>
              {!isConsultantView && extq.open[0] && (
                <p className="text-xs text-slate-600" data-testid="extq-open-detail">
                  {extq.open[0].provider_name || extq.open[0].template_code}:{" "}
                  <strong>{extq.open[0].stats?.missing ?? 0} {t("dash.extqToComplete")}</strong>
                  {extq.open[0].stats?.to_verify ? `, ${extq.open[0].stats.to_verify} ${t("dash.extqToVerify")}` : ""}
                </p>
              )}
              <Link to="/questionari-esterni"
                className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 hover:underline"
                data-testid="goto-external-questionnaires">
                {t("dash.openExtq")} <ArrowRight className="w-3 h-3" aria-hidden="true" />
              </Link>
            </div>
          </WidgetCard>
        )}
      </div>

      <CorrectionsPanel corrections={corrections} readOnly={!canFixCorrections} testid="dashboard-corrections" />

      <Card className="border-green-200 bg-green-50" data-testid="questionnaire-card">        <CardContent className="p-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <ClipboardList className="w-6 h-6 text-green-800 mt-0.5" aria-hidden="true" />
            <div>
              <h2 className="text-base font-semibold text-green-900">{t("dash.qCard.title")}</h2>
              {questionnaire === undefined ? (
                <div className="h-4 w-2/3 rounded bg-green-100 animate-pulse" data-testid="questionnaire-progress-loading" aria-busy="true" />
              ) : questionnaire ? (
                <p className="text-sm text-green-900/80 leading-relaxed" data-testid="questionnaire-progress-text">
                  {t("dash.qCard.status")} <strong data-testid="questionnaire-state-label">{
                    t(`questionnaireState.${questionnaire.questionnaire_state}`) || QUESTIONNAIRE_STATE_LABELS[questionnaire.questionnaire_state]?.label || t("dash.qCard.inProgress")
                  }</strong> — <span title="Percentuale calcolata sulle sole risposte valide"
                    data-testid="dashboard-valid-percentage">{questionnaireSummary?.valid_completion_percentage ?? 0}%</span>{" "}
                  {t("dash.qCard.questions", {
                    answered: questionnaireSummary?.present ?? 0,
                    visible: questionnaireSummary?.applicable ?? 0,
                  })}
                  {questionnaireSummary && (
                    <span className="block text-xs mt-1 text-green-900/70" data-testid="questionnaire-quality-breakdown"
                      title="Risposte presenti ≠ valide: una risposta è valida solo se coerente e senza controlli falliti">
                      Presenti <span data-testid="dashboard-present-count">{questionnaireSummary.present}</span>
                      {" · "}Valide <span data-testid="dashboard-valid-count">{questionnaireSummary.valid}</span>
                      {questionnaireSummary.to_verify > 0 && ` · Da verificare ${questionnaireSummary.to_verify}`}
                      {blockingCount > 0 && (
                        <strong className="text-red-700"> · Validazione: <span data-testid="dashboard-inconsistent-count">{blockingCount}</span> incongruenze bloccanti · Approvazione: Non approvato</strong>
                      )}
                    </span>
                  )}
                  {questionnaireSummary?.valid_completion_percentage === 100 && questionnaire.questionnaire_state === "COMPLETO_DA_INVIARE" && (
                    <span className="block text-xs mt-1" data-testid="questionnaire-full-note">{t("dash.qCard.fullNote")}</span>
                  )}
                </p>
              ) : (
                <p className="text-sm text-green-900/80">{t("dash.qCard.empty")}</p>
              )}
            </div>
          </div>
          <Link to="/questionario" className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-green-700 hover:bg-green-800 rounded-md px-4 py-2 transition-colors" data-testid="goto-questionnaire">
            {questionnaireReadOnly
              ? "Visualizza questionario"
              : questionnaireSummary?.present > 0
                ? t("dash.qCard.continue")
                : t("dash.qCard.start")} <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </CardContent>
      </Card>

      <Card className="border-green-200 bg-green-50" data-testid="next-steps-card">
        <CardContent className="p-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <Users2 className="w-6 h-6 text-green-800 mt-0.5" aria-hidden="true" />
            <div>
              <h2 className="text-base font-semibold text-green-900">{t("dash.nextSteps.title")}</h2>
              <p className="text-sm text-green-900/80 leading-relaxed">
                {t("dash.nextSteps.text")}
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to="/azienda" className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-green-700 hover:bg-green-800 rounded-md px-4 py-2 transition-colors" data-testid="goto-company-profile">
              {t("dash.nextSteps.company")} <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
            <Link to="/periodi" className="inline-flex items-center gap-1.5 text-sm font-semibold text-green-800 bg-white border border-green-300 hover:bg-green-100 rounded-md px-4 py-2 transition-colors" data-testid="goto-periods">
              {t("dash.nextSteps.periods")} <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
